import React, { useState, useCallback, useRef } from "react";
import LiveStreamProductsSelection from "./LiveStreamProductsSelection";
import SessionList from "./SessionList";
import LiveStream from "./LiveStream";
import Loader from "../../components/LoaderComponent";
import { IData, ISession } from "../../lib/interfaces";
import { useOutletContext } from "react-router-dom";
import { BroadcastManagerProvider } from "./components/BroadcastManagerContext";
import { useUpdateLiveSessionMutation } from "../../api/api";

interface LayoutContext {
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const GoLivePage: React.FC = () => {
  const videoRef = useRef<HTMLCanvasElement | null>(null);
  const [updateLiveSession] = useUpdateLiveSessionMutation();
  const [selectedSession, setSelectedSession] = useState<ISession | null>(null);
  const [refetchSessions, setRefetchSessions] = useState<
    (() => Promise<void>) | null
  >(null);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [selectedData, setSelectedData] = useState<IData | null>(null);
  const [loading, setLoading] = useState(false);
  const { setShowAlertOnRouteChange } = useOutletContext<LayoutContext>();
  const [isLive, setIsLive] = useState<Boolean>(false);
  const [updating, setUpdating] = useState(false);

  const handleSessionSelect = (session: ISession | null) => {
    setSelectedSession(session);
    setShowCreateSession(true);
    setShowAlertOnRouteChange(true);
  };

  const handleRefetchSessions = useCallback(
    (refetchFn: () => Promise<void>) => {
      setRefetchSessions(() => refetchFn);
    },
    []
  );

  const handleRefetchSessionsOnUpdate = useCallback(async () => {
    try {
      if (refetchSessions) await refetchSessions();
    } catch (error) {
      console.error("Error refetching sessions:", error);
    }
  }, [refetchSessions]);

  const handleSessionUpdated = useCallback(async () => {
    try {
      if (refetchSessions) await refetchSessions();
    } catch (error) {
      console.error("Error refetching sessions:", error);
    } finally {
      setShowCreateSession(false);
      setSelectedSession(null);
      setShowAlertOnRouteChange(false);
    }
  }, [refetchSessions]);

  return (
    <BroadcastManagerProvider
      videoRef={videoRef}
      updateLiveSession={updateLiveSession}
      selectedData={selectedData}
      setSelectedData={setSelectedData}
      handleSessionUpdated={handleSessionUpdated}
      setLoading={setLoading}
      setIsLive={setIsLive}
      setUpdating={setUpdating}
    >
      <div className="flex flex-1 h-[calc(100vh-80px)] overflow-hidden">
        {loading && (
          <Loader
            loading={videoRef.current ? "Ending Stream" : "Starting Stream"}
          />
        )}
        {updating && <Loader loading={"Updating..."} />}
        <div className="flex flex-1 w-full">
          <div
            className={`${
              showCreateSession || isLive ? "hidden md:block" : "block w-full"
            } md:w-1/3 h-screen border-r border-gray-200 overflow-hidden`}
          >
            <div className="h-[89vh] overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <SessionList
                  onSessionSelect={handleSessionSelect}
                  refetchSessions={handleRefetchSessions}
                  onCreateSession={() => setShowCreateSession(true)}
                  refetchSessionsOnUpdate={handleRefetchSessionsOnUpdate}
                  isLiveStreamStarted={isLive}
                  setIsLiveStreamStarted={setIsLive}
                />
              </div>
            </div>
          </div>

          {isLive ? (
            <div className="block w-full md:w-2/3 h-screen bg-gray-50 overflow-hidden">
              <div className="h-full overflow-y-auto border border-red-50">
                <LiveStream videoRef={videoRef} />
              </div>
            </div>
          ) : (
            <div
              className={`${
                !showCreateSession ? "hidden md:block" : "block w-full"
              } md:w-2/3 h-screen bg-gray-50 overflow-hidden`}
            >
              <div className="h-full overflow-y-auto border border-red-50 ">
                <LiveStreamProductsSelection
                  sessionToReschedule={selectedSession}
                  onSessionUpdated={handleSessionUpdated}
                  onBack={() => setShowCreateSession(false)}
                  refetchSessionsOnUpdate={handleRefetchSessionsOnUpdate}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </BroadcastManagerProvider>
  );
};

export default GoLivePage;
